import React, { useState, ChangeEvent, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAppDispatch } from "@/app/hooks";
import { validateStudentsList } from "@/features/users/usersThunks";
import { selectValidatedStudents, selectUsersError, clearValidatedStudents } from "@/features/users/usersSlice";
import { useNavigate } from "react-router-dom";
import RelatedStudents from "../components/RelatedStudents";
import { FormData, FormErrors, validateForm } from "./validations";
import { Student } from "@/types/user.types";
import { AuthAlerts } from "../components/Alerts";
import { useSelector } from "react-redux";
import { selectAuthError, selectAuthUser } from "@/features/auth/authSelectors";
import Google from "@mui/icons-material/Google";
import { GoogleRegisterAdditionalData } from "../components/GoogleRegisterAdditionalData";
import { useAuth } from "@/hooks/useAuth";
import { clearError } from "@/features/auth/authSlice";

const CreateAccountForm: React.FC = () => {
  const { register, registerGoogleAccount, isLoading } = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const validatedStudents = useSelector(selectValidatedStudents);
  const usersError = useSelector(selectUsersError);
  const registerError = useSelector(selectAuthError);
  const authUser = useSelector(selectAuthUser);

  const [formData, setFormData] = useState<FormData>({
    name: "",
    documentType: "CC",
    documentNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [relatedStudents, setRelatedStudents] = useState<Student[]>([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error" | "info">("success");
  const [googleModalOpen, setGoogleModalOpen] = useState(false);

  const showAlert = (message: string, severity: "success" | "error" | "info") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  useEffect(() => {
    if (usersError) {
      showAlert(usersError, "error");
    }
  }, [usersError]);

  useEffect(() => {
    if (authUser) {
      showAlert("¡Cuenta creada exitosamente! Redirigiendo...", "success");
      setFormData({
        name: "",
        documentType: "CC",
        documentNumber: "",
        email: "",
        password: "",
        confirmPassword: "",
      });
      setRelatedStudents([]);
      dispatch(clearValidatedStudents());
      setTimeout(() => navigate("/login"), 2000);
    } else {
      if (registerError) {
        let errorMessage = registerError;
        if (registerError.includes("auth/email-already-in-use")) {
          errorMessage = "Este email ya está registrado";
        } else if (registerError.includes("auth/weak-password")) {
          errorMessage = "La contraseña es demasiado débil";
        } else if (registerError.includes("auth/invalid-email")) {
          errorMessage = "El email no es válido";
        }
        showAlert(errorMessage, "error");
        dispatch(clearError());
      }
    }
  }, [authUser, registerError]);

  const handleAddStudent = async (newStudent: Student) => {
    const student: Student = {
      id: newStudent.id,
      name: newStudent.name.trim(),
      documentId: newStudent.documentId.trim(),
    };

    const updatedStudents = [...relatedStudents, student];
    const documentIds = updatedStudents.map(s => s.documentId);

    try {
      const validatedStudentsResult = await dispatch(validateStudentsList(documentIds)).unwrap();

      if (validatedStudentsResult) {
        setRelatedStudents(updatedStudents);
      }
    } catch (error: any) {
      const errorMessage = error?.message || error || "Error al validar estudiante";
      showAlert(errorMessage, "error");
    }
  };

  const handleDeleteStudent = (id: string) => {
    setRelatedStudents((prevStudents) => prevStudents.filter((student) => student.id !== id));
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Limpiar error del campo cuando el usuario empieza a escribir
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = () => {
    const result = validateForm({ formData, setFormErrors })

    if (!result) {
      return
    }

    if (relatedStudents.length === 0) {
      showAlert("Debes agregar al menos un estudiante", "error");
      return;
    }

    // Students are already validated in handleAddStudent, proceed with registration
    if (validatedStudents && validatedStudents.length > 0) {
      register(
        formData.email,
        formData.password,
        {
          displayName: formData.name,
          identifier: formData.documentNumber,
          identifierType: formData.documentType,
          phoneNumber: '',
          role: 'Attendant',
          site: '',
          relatedStudents: validatedStudents,
        }
      );
    }
  };

  const handleGoogleRegister = () => {
    setGoogleModalOpen(true);
  };

  const handleGoogleContinue = async (
    data: { documentType: string; documentNumber: string },
    students: Student[]
  ) => {
     
    try {
      await registerGoogleAccount(
        {
          identifier: data.documentNumber,
          identifierType: data.documentType,
          phoneNumber: '',
          role: 'attendant',
          site: '',
          relatedStudents: students,
        }
      );
      setGoogleModalOpen(false);
    } catch (error: any) {
      ;
      console.error('Error registering with Google:', error);
      setAlertMessage(error || 'Error al registrar con Google');
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "90vh",
        height: "100%",
        backgroundColor: "#f7f7f7",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          alignItems: "start",
          justifyContent: "start",
          gap: 4,
        }}
      >
        {/* Imagen */}
        <Box
          sx={{
            width: "45%",
            height: "90vh",
            alignItems: "center",
            display: { xs: "none", md: "flex" },
          }}
        >
          <Box
            component="img"
            src="/images/img2.jpg"
            alt="register illustration"
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: 2,
              objectFit: "cover",
            }}
          />
        </Box>

        {/* Formulario */}
        <Paper
          elevation={1}
          sx={{
            width: { xs: "100%", md: "55%" },
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
            height: "100%",
            px: 4
          }}
        >
          <Box sx={{ p: 4, pb: 2, textAlign: "center" }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Crea tu cuenta
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ¿Eres acudiente? Crea tu cuenta para realizar pagos
              institucionales de forma rápida y segura desde cualquier lugar.
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 4,
              pb: 4,
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f5f5f5",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c1c1",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#a8a8a8",
                },
              },
            }}
          >
            {/* Nombre */}
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mb: 0.5, color: "text.primary" }}
            >
              Nombre <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="name"
              placeholder="Ingrese Nombre"
              value={formData.name}
              onChange={handleChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
              disabled={isLoading}
              InputProps={{
                sx: {
                  backgroundColor: "#fafafa",
                  mb: formErrors.name ? 1 : 2,
                  borderRadius: "8px",
                  "& fieldset": { border: "none" },
                },
              }}
            />

            {/* Documento de Identidad */}
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mb: 0.5, color: "text.primary" }}
            >
              Documento de Identidad <span style={{ color: "red" }}>*</span>
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <Select
                name="documentType"
                value={formData.documentType}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    documentType: e.target.value,
                  }))
                }
                disabled={isLoading}
                sx={{
                  width: "100px",
                  backgroundColor: "#fafafa",
                  borderRadius: "8px",
                  "& fieldset": { border: "none" },
                }}
              >
                <MenuItem value="CC">CC</MenuItem>
                <MenuItem value="TI">TI</MenuItem>
                <MenuItem value="CE">CE</MenuItem>
                <MenuItem value="PAS">PAS</MenuItem>
              </Select>
              <TextField
                fullWidth
                name="documentNumber"
                placeholder="Número"
                value={formData.documentNumber}
                onChange={handleChange}
                error={!!formErrors.documentNumber}
                helperText={formErrors.documentNumber}
                disabled={isLoading}
                InputProps={{
                  sx: {
                    backgroundColor: "#fafafa",
                    borderRadius: "8px",
                    "& fieldset": { border: "none" },
                  },
                }}
              />
            </Box>
            <RelatedStudents
              students={relatedStudents}
              handleAddStudent={handleAddStudent}
              handleDeleteStudent={handleDeleteStudent}
            />
            {/* Email */}
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mb: 0.5, color: "text.primary" }}
            >
              Email <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="email"
              type="email"
              placeholder="Ingrese email"
              value={formData.email}
              onChange={handleChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              disabled={isLoading}
              InputProps={{
                sx: {
                  backgroundColor: "#fafafa",
                  mb: formErrors.email ? 1 : 2,
                  borderRadius: "8px",
                  "& fieldset": { border: "none" },
                },
              }}
            />

            {/* Contraseña */}
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mb: 0.5, color: "text.primary" }}
            >
              Contraseña <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Mínimo 8 caracteres"
              value={formData.password}
              onChange={handleChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              disabled={isLoading}
              InputProps={{
                sx: {
                  backgroundColor: "#fafafa",
                  mb: formErrors.password ? 1 : 2,
                  borderRadius: "8px",
                  "& fieldset": { border: "none" },
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Repite Contraseña */}
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mb: 0.5, color: "text.primary" }}
            >
              Repite Contraseña <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirme su contraseña"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
              disabled={isLoading}
              InputProps={{
                sx: {
                  backgroundColor: "#fafafa",
                  mb: 3,
                  borderRadius: "8px",
                  "& fieldset": { border: "none" },
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Botón de Registro */}
            <Button
              fullWidth
              disabled={isLoading}
              onClick={handleSubmit}
              sx={{
                py: 1.5,
                backgroundColor: "secondary.main",
                "&:hover": { backgroundColor: "secondary.500" },
                "&:disabled": { backgroundColor: "#e0e0e0" },
                color: "#fff",
                fontWeight: 600,
                fontSize: "1rem",
                textTransform: "none",
                position: "relative",
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1, color: "#fff" }} />
                  Creando cuenta...
                </>
              ) : (
                "Registrarse"
              )}
            </Button>

            <Divider sx={{ color: "grey", my: 3 }}>O</Divider>

            {/* Botón de Google Sign-in */}
            <Button
              fullWidth
              variant="outlined"
              disabled={isLoading}
              onClick={handleGoogleRegister}
              startIcon={<Google />}
              sx={{
                py: 1.5,
                borderRadius: "8px",
                borderColor: "primary.main",
                backgroundColor: "#fff",
                color: "primary.dark",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                "&:hover": {
                  backgroundColor: "#f8fbff",
                  borderColor: "primary.dark",
                },
                "&:disabled": {
                  backgroundColor: "#fafafa",
                  color: "text.disabled",
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: "primary.main" }} />
              ) : (
                "Registrarse con Google"
              )}
            </Button>
          </Box>
        </Paper>
      </Container>

      <AuthAlerts alertOpen={alertOpen} setAlertOpen={setAlertOpen} alertSeverity={alertSeverity} alertMessage={alertMessage} />

      <GoogleRegisterAdditionalData
        openModal={googleModalOpen}
        handleCloseModal={() => setGoogleModalOpen(false)}
        handleContinue={handleGoogleContinue}
      />
    </Box>
  );
};

export default CreateAccountForm;
